import { types, verify } from "@fraction/shared";
import { type ReactNode, useCallback, useMemo } from "react";
import { type Accept, useDropzone } from "react-dropzone";
import { cn } from "src/utilities/shadcnUtils";

export type FileWithEnumType = File & { type: types.MimeType };

/** What a document upload surface takes: the formats the pipeline can read a document out of. */
export const DOCUMENT_UPLOAD_MIME_TYPES: types.MimeType[] = [
  types.MimeType.PDF,
  types.MimeType.PNG,
  types.MimeType.JPEG,
  types.MimeType.HEIC,
];

/** The same set as react-dropzone's `accept`, extensions taken from the shared file catalog. */
export const DOCUMENT_UPLOAD_ACCEPT: Accept = DOCUMENT_UPLOAD_MIME_TYPES.reduce(
  (accept, mimeType) => ({ ...accept, [mimeType]: types.FILE_TYPES[mimeType].extensions }),
  {} as Accept
);

/** Flattened, for the surfaces that recognise a file by its name rather than its mime type. */
export const DOCUMENT_UPLOAD_EXTENSIONS: string[] = DOCUMENT_UPLOAD_MIME_TYPES.flatMap(
  (mimeType) => types.FILE_TYPES[mimeType].extensions
);

const Dropzone = <Multi extends boolean>({
  accept,
  className,
  onDrop,
  // @ts-expect-error
  multi = true,
  children,
  dragActiveChildren,
  disabled,
  noClick,
  noDragEventsBubbling,
}: {
  accept?: types.MimeType[];
  className?: string;
  onDrop: (acceptedFiles: Multi extends true ? FileWithEnumType[] : FileWithEnumType) => void;
  multi: Multi;
  children?: ReactNode;
  dragActiveChildren?: ReactNode;
  disabled?: boolean;
  /** Disable opening the file dialog on click — drag-and-drop only. */
  noClick?: boolean;
  /**
   * Stop drag/drop events from bubbling to ancestor dropzones. Pass `true` on
   * inner zones (chat upload bubble, side-panel tray) so a drop captured by
   * the inner one doesn't *also* fire the page-wide dropzone's onDrop.
   */
  noDragEventsBubbling?: boolean;
}) => {
  const handleOnDrop = useCallback(
    (acceptedFiles: File[]) => {
      const assertedFiles = acceptedFiles.filter((f) =>
        // This verification should always be true with accepted files that can only be part of the enum.
        verify.isEnum(types.MimeType, f.type)
      ) as FileWithEnumType[];

      if (multi) {
        // @ts-expect-error
        onDrop(assertedFiles);
      } else {
        // @ts-expect-error
        onDrop(assertedFiles[0]);
      }
    },
    [onDrop, multi]
  );

  const dropzoneAccept: Accept | undefined = useMemo(
    () => accept?.reduce((acc, value) => ({ ...acc, [value]: types.FILE_TYPES[value].extensions }), {}),
    [accept]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    maxFiles: multi ? undefined : 1,
    onDrop: handleOnDrop,
    accept: dropzoneAccept,
    disabled,
    noClick,
    noDragEventsBubbling,
  });
  return (
    <div
      {...getRootProps()}
      // `relative` so react-dropzone's hidden absolute-positioned <input> is
      // anchored to this container instead of escaping all the way up to <html>.
      // Without it, every Dropzone on the page contributes an absolute input
      // sitting at its "natural" document position, which can blow up
      // documentElement.scrollHeight and break layouts that rely on the page
      // not being scrollable (e.g. /apply).
      className={cn("relative", className)}
    >
      <input {...getInputProps()} />
      <div
        className={cn(
          "h-full flex justify-center items-center text-gray-600 text-[0.9em] cursor-pointer hover:text-gray-500",
          disabled && "cursor-default"
        )}
      >
        {isDragActive ? (
          dragActiveChildren ? (
            dragActiveChildren
          ) : (
            <i>Drop the files here ...</i>
          )
        ) : children ? (
          children
        ) : (
          <i>Drop files here, or click to select files</i>
        )}
      </div>
    </div>
  );
};

export default Dropzone;
